import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { INITIAL_PAYMENT_MODE_INFO_COL_DEF, transferRowData } from "presentation/constant/CounterCustomerCharge/PaymentModeAndAdditionInfoColumnDefinition";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { usePaymentModeUnloadGuard } from "presentation/hook/CounterCustomerCharge/usePaymentModeUnloadGuard";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { CommonMessageModal } from "presentation/view/components/CommonMessageModal";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NbisDateTimePickerComponent from "presentation/view/components/NbisDateTimePickerComponent";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { FieldType, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, SidebarTitle, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

export const PaymentModeAndAdditionInfoTablePanel = () => {
    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const { paymentModeState, isShowAddPaymentModePanel, dynamicOptions } = counterCustomerChargeState;
    const { isAdd, allFormState, paymentModeHeader, tableData, isSaveClicked, isAgentDate, isCustomsDec } = paymentModeState;
    const gridRef: any = useRef();
    const tableId = 'paymen-mode-addition-info-table';
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant.PAYMENT;
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;
    const [isShowApply, setIsShowApply] = useState<boolean>(false);
    // const [agentDateAfterTimezoneSpecMap, setAgentDateAfterTimezoneSpecMap] = useState<{ [key: string]: object }>({});

    // Guard against an abnormal close (tab / browser closed): prompt the user and roll back the
    // payment if they confirm. It is switched off once the payment has been applied successfully.
    const [isUnloadGuardEnabled, setIsUnloadGuardEnabled] = useState<boolean>(true);
    usePaymentModeUnloadGuard(paymentModeState.paymentModeHeader.docHdrId, isUnloadGuardEnabled);

    const handApply = useCallback(async () => {
        setIsShowApply(false);
        setIsLoading(true);
        const selectedRows = counterCustomerChargeState.selectedRows.length > 0 ? counterCustomerChargeState.selectedRows : counterCustomerChargeState.tableData
        counterCustomerChargeVM.onPaymentModeApply(paymentModeHeader, operatingUnit, isAgentDate, isCustomsDec).then(async (res) => {
            if (res && res.success) {
                // The payment is settled, closing the page must not roll it back anymore.
                setIsUnloadGuardEnabled(false);
                // setAgentDateAfterTimezoneSpecMap({});
                messageBarVM.showSuccess('Apply Successfully.');
                await counterCustomerChargeVM.onSearchCounterCustomer(counterCustomerChargeState.searchCriteria);
                if (operatingUnit === 'AQCT' || operatingUnit === 'RSCT') {
                    const ids: number[] = selectedRows?.filter(x => x.dtlState !== 'DI' && x.dtlState !== 'I').map(x => x.id);
                    await counterCustomerChargeVM.previewDraftInvoice(ids);
                }
            } else {
                if (res.data && res.data !== 'missing') {
                    messageBarVM.showError('Apply Failed.');
                    await counterCustomerChargeVM.onSearchCounterCustomer(counterCustomerChargeState.searchCriteria);
                }
            }
            setIsLoading(false);
        });
    }, [counterCustomerChargeState.selectedRows, counterCustomerChargeState.tableData, counterCustomerChargeState.searchCriteria, operatingUnit, counterCustomerChargeVM, paymentModeHeader, isAgentDate, isCustomsDec, messageBarVM]);

    const handleApplyClick = useCallback(async () => {
        if (!tableData || tableData.length < 1) {
            setIsShowApply(false);
            setIsUnloadGuardEnabled(false);
            counterCustomerChargeVM.closeCompDialogForPayment(paymentModeState.paymentModeHeader.docHdrId);
            return;
        } else {
            let paymentAmount = 0;
            tableData.forEach(item => {
                if (item.paymentAmount) {
                    paymentAmount = (Math.round(paymentAmount * 100) + Math.round(item.paymentAmount * 100)) / 100;
                }
            })
            if (paymentModeHeader.invoiceAmount && paymentModeHeader.invoiceAmount > paymentAmount) {
                messageBarVM.showError("Insufficient fund for settling the invoices.");
                return;
            }

            if (isCustomsDec && paymentModeHeader.customsDeclarationDate && paymentModeHeader.customsDeclarationDate < new Date()) {
                messageBarVM.showError("The DO validity date time should not be earlier than the current system date");
                return;
            }
            if (isAgentDate && paymentModeHeader.agentDeliveryDate && paymentModeHeader.agentDeliveryDate < new Date()) {
                messageBarVM.showError("The DO validity date time should not be earlier than the current system date");
                return;
            }

        }
        let collHeaderList = await counterCustomerChargeVM.getCollectionHdr(paymentModeHeader, counterCustomerChargeState);

        let totalPaymentAmt = 0;
        let totalInvoiceAmt = 0;
        if (collHeaderList && collHeaderList.length > 0) {
            collHeaderList.forEach(entity => {
                if (entity.totalPaymentAmount) {
                    totalPaymentAmt = (Math.round(totalPaymentAmt * 100) + Math.round(entity.totalPaymentAmount * 100)) / 100;
                }
                if (entity.totalAmount) {
                    totalInvoiceAmt = (Math.round(totalInvoiceAmt * 100) + Math.round(entity.totalAmount * 100)) / 100;
                }
            })
        }

        if ((totalPaymentAmt - totalPaymentAmt > 0) || totalPaymentAmt === 0) {
            return;
        }

        let returnPaymentAmount = await counterCustomerChargeVM.onCalculatePayment(collHeaderList, counterCustomerChargeState);
        if (Number(returnPaymentAmount) > 0) {
            counterCustomerChargeVM.onCopyPayAmount(Math.round(Number(returnPaymentAmount) * 100) / 100);
            setIsShowApply(true);
        } else {
            handApply();
        }

    }, [handApply, tableData, counterCustomerChargeVM, paymentModeHeader, counterCustomerChargeState, paymentModeState.paymentModeHeader.docHdrId, messageBarVM, isAgentDate, isCustomsDec]);

    const handleCancel = useCallback(() => {
        setIsShowApply(false);
        counterCustomerChargeVM.handleCancelClick();
        return;
    }, [counterCustomerChargeVM])

    const handleAddClick = useCallback(() => {
        counterCustomerChargeVM.onPaymentModeAdd(operatingUnit);
    }, [counterCustomerChargeVM, operatingUnit])

    const handleClose = useCallback(() => {
        // Normal close already rolls the rows back, so the unload guard must not fire again.
        setIsUnloadGuardEnabled(false);
        counterCustomerChargeVM.closeCompDialogForPayment(paymentModeState.paymentModeHeader.docHdrId);
        // setAgentDateAfterTimezoneSpecMap({});
    }, [counterCustomerChargeVM, paymentModeState.paymentModeHeader.docHdrId])

    const handleReset = useCallback(() => {
        counterCustomerChargeVM.onResetForPayment();
    }, [counterCustomerChargeVM]);

    // useEffect(() => {
    //     if (("AQCT" === operatingUnit || "RSCT" === operatingUnit)
    //         && paymentModeHeader.agentDeliveryDate) {
    //         counterCustomerChargeVM.checkDateSwitchTimezoneRange(paymentModeHeader.agentDeliveryDate, timezone).then((data) => {
    //             if (data && data !== undefined) {
    //                 setAgentDateAfterTimezoneSpecMap(data);
    //             } else {
    //                 setAgentDateAfterTimezoneSpecMap({});
    //             }
    //         });
    //     }
    // }, [counterCustomerChargeVM, timezone, paymentModeHeader.agentDeliveryDate, operatingUnit, isShowTimezoneSpecification]);

    const memoInvoiceAmount = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: '240px', maxWidth: '240px', width: "20%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={(paymentModeHeader.invoiceAmount ?? 0).toFixed(2)}
                fieldValue={paymentModeHeader.invoiceAmount}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.INVOICE_AMOUNT}
                fieldType={FieldType.TEXT}
                fieldKey={'invoiceAmount'}
                maxLength={60}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => counterCustomerChargeVM.onPaymentModeFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} requiredFieldList={[]} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.INVOICE_AMOUNT, paymentModeHeader.invoiceAmount, counterCustomerChargeVM])

    const memoShippingAgent = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: '240px', maxWidth: '240px', width: "20%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={operatingUnit !== 'AQCT' && operatingUnit !== 'RSCT'}
                readOnlyValue={paymentModeHeader.shippingAgent || ''}
                fieldValue={paymentModeHeader.shippingAgent}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPING_AGENT}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'shippingAgent'}
                maxLength={60}
                options={dynamicOptions.agentNoDropdownOptions}
                isSaveClicked={isSaveClicked}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPING_AGENT, paymentModeHeader.shippingAgent, counterCustomerChargeVM, dynamicOptions.agentNoDropdownOptions, operatingUnit, allFormState, isSaveClicked])

    const memoCustomsDeclarationNo = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: '240px', maxWidth: '240px', width: "30%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={!isCustomsDec}
                readOnlyValue={paymentModeHeader.customsDeclarationNo || ''}
                fieldValue={paymentModeHeader.customsDeclarationNo}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.CUSTOMS_DECLARATION_NO}
                isSaveClicked={paymentModeState.isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'customsDeclarationNo'}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isCustomsDec, paymentModeHeader.customsDeclarationNo, COUNTER_CUSTOMER_CHARGE_CONSTANT.CUSTOMS_DECLARATION_NO, paymentModeState.isSaveClicked, counterCustomerChargeVM])

    const memoCustomsDeclarationDate = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: '240px', maxWidth: '240px', width: "30%", marginBottom: "24px" }}>
            <NbisDateTimePickerComponent
                disabled={false}
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.CUSTOMS_DECLARATION_DATE}
                optional={!isCustomsDec}
                width="240px"
                date={paymentModeHeader.customsDeclarationDate}
                fieldName="customsDeclarationDate"
                errorMessage={allFormState?.customsDeclarationDate}
                onDateChange={counterCustomerChargeVM.onPaymentModeFieldChange} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.CUSTOMS_DECLARATION_DATE, isCustomsDec, paymentModeHeader.customsDeclarationDate, allFormState?.customsDeclarationDate, counterCustomerChargeVM.onPaymentModeFieldChange])

    const memoAgentDeliveryDate = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: '240px', maxWidth: '240px', width: "20%", marginBottom: "24px" }}>
            <NbisDateTimePickerComponent
                disabled={false}
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_DELIVERY_DATE}
                optional={!isAgentDate}
                width="230px"
                date={paymentModeHeader.agentDeliveryDate}
                fieldName="agentDeliveryDate"
                errorMessage={allFormState?.agentDeliveryDate}
                onDateChange={counterCustomerChargeVM.onPaymentModeFieldChange} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_DELIVERY_DATE, isAgentDate, paymentModeHeader.agentDeliveryDate, allFormState?.agentDeliveryDate, counterCustomerChargeVM])

    const memoObservation = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: '240px', maxWidth: '520px', width: "65%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={paymentModeHeader.observation || ''}
                fieldValue={paymentModeHeader.observation}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.OBSERVATION}
                fieldType={FieldType.TEXTAREA}
                fieldKey={'observation'}
                maxLength={200}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => counterCustomerChargeVM.onPaymentModeFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} requiredFieldList={[]} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.OBSERVATION, paymentModeHeader.observation, counterCustomerChargeVM])

    /*const template = useMemo(() => {
        return (
            <>
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ marginRight: "5px", display: "flex" }}>
                        <HPHButton disabled={isAdd} label={WorkspaceConstant.Common.BUTTON_APPLY} size={'Small'} theme={'Primary'} onClick={handleApplyClick} />
                        <CommonMessageModal headerText={""}
                            contentText={"The payment amount for service charge is " + paymentModeState.servicePayAmount + " . Do you want to proceed?"}
                            visible={isShowApply}
                            footerButtons={[
                                { label: 'Yes', theme: 'Primary', onClick: handApply },
                                { label: 'No', theme: 'Secondary', onClick: handleCancel },
                            ]}
                        />
                    </div>
                </div>
            </>
        )
    }, [handApply, handleApplyClick, handleCancel, isAdd, isShowApply, paymentModeState.servicePayAmount])*/

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoPaymentModeAdditionInfoHeader = useMemo(() => {
        return <>
            <Sidebarheader style={{ width: '100%', marginLeft: "15px" }}>
                <div className={'add-edit-form'} style={{ height: '40vh', maxHeight: '40vh', overflow: 'auto' }}>
                    <div className="flex-row-auto">
                        <div className={'flex-row-item'}>
                            <CriteriaItemContainer>
                                {memoInvoiceAmount}
                                {memoShippingAgent}
                                {(operatingUnit === 'AQCT' || operatingUnit === 'RSCT') &&
                                    <>
                                        {memoAgentDeliveryDate}
                                    </>
                                }
                            </CriteriaItemContainer>

                            {(operatingUnit === 'AQCT' || operatingUnit === 'RSCT') &&
                                <CriteriaItemContainer>
                                    {memoCustomsDeclarationNo}
                                    {memoCustomsDeclarationDate}
                                </CriteriaItemContainer>
                            }

                            <CriteriaItemContainer>
                                {memoObservation}
                            </CriteriaItemContainer>
                        </div>
                    </div>
                </div>
            </Sidebarheader>
        </>
    }, [memoAgentDeliveryDate, memoCustomsDeclarationDate, memoCustomsDeclarationNo, memoInvoiceAmount, memoObservation, memoShippingAgent, operatingUnit])

    const memoPaymentModeAdditionInfoTable = useMemo(() => {
        return <NbisTable
            id={tableId}
            isNewColumnSetting={true}
            columns={INITIAL_PAYMENT_MODE_INFO_COL_DEF?.slice()}
            data={transferRowData(paymentModeState.tableData)}
            showPaginator={false}
            editable={false}
            showAddIcon={!isAdd}
            onAddClick={handleAddClick}
            showDeleteButton={false}
            showReloadIcon={false}
            isScrollHighlighted={true}
            isRowHighligted={true}
            selectionMode={false}
            gridHeight="customHeight"
            customHeight="calc(43vh)"
            ref={gridRef}
        // headerLabel={template}
        />
    }, [paymentModeState.tableData, handleAddClick, isAdd])

    return <>
        <div className={`main-comp-wrapper`}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{CounterCustomerChargeMaintenanceConstant.PAYMENT.TITLE}</SidebarTitle>
                        <StyledAction>
                            <>
                                <IconButton fileName="Icon-reset" size="medium" disabled={isShowAddPaymentModePanel} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Reset'} onClick={() => handleReset()} />
                                <IconButton fileName="Icon-cross" size="medium" disabled={isShowAddPaymentModePanel} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Close'} onClick={() => handleClose()} />
                                <IconButton fileName="Icon-tick" size="medium" disabled={isShowAddPaymentModePanel} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={() => handleApplyClick()} />
                            </>
                        </StyledAction>
                    </Sidebarheader>
                    {isShowApply &&
                        <CommonMessageModal headerText={""}
                            contentText={"The payment amount for service charge is " + paymentModeState.servicePayAmount + " . Do you want to proceed?"}
                            visible={isShowApply}
                            footerButtons={[
                                { label: 'Yes', theme: 'Primary', onClick: handApply },
                                { label: 'No', theme: 'Secondary', onClick: handleCancel },
                            ]}
                        />}
                </div>
            </div>
            {memoPaymentModeAdditionInfoHeader}
            {isLoading && <Loader Indicator="Spinner" size="Medium" />}
            <TableWrapper>
                {memoPaymentModeAdditionInfoTable}
            </TableWrapper>
        </div>
    </>;
}

export default memo(PaymentModeAndAdditionInfoTablePanel);
